import { useId, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Area, AreaChart } from 'recharts';

import { DailyUsagePointDto } from '@/Domain/api/Analytics/dto/KBSources.dto';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { formatUsageShare } from '@/UI/Insights/KBSources/usageShare';

type Props = {
  data: DailyUsagePointDto[] | undefined;
  width?: number;
  height?: number;
};

export const UsageSparkline = ({ data, width = 160, height = 48 }: Props) => {
  const { t } = useTranslation('insights_page', { keyPrefix: 'kb_sources.sparkline' });
  const gradientId = useId().replace(/:/g, '');

  const { hasData, isSinglePoint } = useMemo(() => {
    if (!data || !data.length) {
      return { hasData: false, isSinglePoint: false };
    }

    const shares = data.map((d) => d.share_percent);
    const hasAnyData = shares.some((s) => s > 0);

    if (!hasAnyData) {
      return { hasData: false, isSinglePoint: false };
    }

    return { hasData: true, isSinglePoint: data.length === 1 };
  }, [data]);

  const summary = useMemo(() => {
    if (!data || !data.length) return null;

    // Volume-weighted, not the mean of the daily shares: an unweighted mean lets a day with
    // a single reference count as much as a day with hundreds, so it contradicted the row's
    // own Usage Share by up to 8pp (and overstated sparse articles by ~67%).
    const totalUsage = data.reduce((acc, d) => acc + d.usage_count, 0);
    const totalRefs = data.reduce((acc, d) => acc + d.total_count, 0);
    const avgShare = totalRefs > 0 ? (totalUsage / totalRefs) * 100 : 0;
    const maxShareDay = data.reduce(
      (max, d) => (d.share_percent > max.share_percent ? d : max),
      data[0],
    );

    return {
      avgShare: formatUsageShare(avgShare, totalUsage),
      peakShare: formatUsageShare(maxShareDay.share_percent, maxShareDay.usage_count),
      peakDate: maxShareDay.date,
      startDate: data[0].date,
      endDate: data[data.length - 1].date,
    };
  }, [data]);

  const trendPeriodLabel = summary && (
    <div className="text-muted-foreground">
      {summary.startDate === summary.endDate
        ? t('trend_day', { date: summary.startDate })
        : t('trend_period', { startDate: summary.startDate, endDate: summary.endDate })}
    </div>
  );

  if (!hasData) {
    return (
      <div
        className="flex items-center justify-center text-foreground-subtle text-xs"
        style={{ width, height }}
      >
        --
      </div>
    );
  }

  if (isSinglePoint) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={200}>
          <TooltipTrigger asChild>
            <div
              data-testid="kb-usage-trend"
              className="flex items-center justify-center cursor-pointer rounded-lg hover:bg-muted transition-colors duration-200"
              style={{ width, height }}
            >
              <div className="w-2.5 h-2.5 rounded-full bg-primary" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="top" className="text-xs">
            {summary && (
              <div className="flex flex-col gap-1">
                <div>
                  <span className="text-muted-foreground">{t('avg_share')}</span>{' '}
                  <span className="font-medium tabular-nums">{summary.avgShare}</span>
                </div>
                {trendPeriodLabel}
              </div>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div
            data-testid="kb-usage-trend"
            className="cursor-pointer rounded-lg px-2 py-1 hover:bg-muted transition-all duration-200"
            style={{ width: width + 16, height: height + 8 }}
          >
            <AreaChart
              width={width}
              height={height}
              data={data}
              margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
            >
              <defs>
                <linearGradient id={`gradient-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="var(--primary)" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="share_percent"
                stroke="var(--primary)"
                strokeWidth={1.5}
                fill={`url(#gradient-${gradientId})`}
                isAnimationActive={false}
                dot={false}
                activeDot={{
                  r: 3,
                  fill: 'var(--primary)',
                  stroke: 'var(--card)',
                  strokeWidth: 2,
                }}
              />
            </AreaChart>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          {summary && (
            <div className="flex flex-col gap-1">
              <div>
                <span className="text-muted-foreground">{t('avg_share')}</span>{' '}
                <span className="font-medium tabular-nums">{summary.avgShare}</span>
              </div>
              <div>
                <span className="text-muted-foreground">{t('peak')}</span>{' '}
                <span className="font-medium tabular-nums">{summary.peakShare}</span>
                <span className="text-muted-foreground">
                  {' '}
                  {t('peak_on', { date: summary.peakDate })}
                </span>
              </div>
              {trendPeriodLabel}
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
